import { DateValue } from '@mantine/dates';
import { DATE_RANGE_INTERVALS, DateRangeKey } from '@newo/ui-kit';
import { CHANNEL_TO_INTEGRATIONS, INTEGRATION_TO_CHANNEL } from '@/constants';
import dayjs from '@/libs/dayjs';
import type { TChannel } from '@/types/common.ts';

export const allChannels = Object.values(CHANNEL_TO_INTEGRATIONS).flat();

export const stringToBoolean = (value?: string) => {
    if (value === 'true') {
        return true;
    }
    if (value === 'false') {
        return false;
    }
};

export const booleanToString = (value?: boolean) => {
    if (value === true) {
        return 'true';
    }
    if (value === false) {
        return 'false';
    }
};

export const localDateToUTC = (date: Date) => {
    return dayjs(date).utc().toISOString();
};

export const utcDateToLocal = (date: string) => {
    return dayjs(date).local().toDate();
};

/**
 * Converts an array of channels to their corresponding integrations
 * @param channels - Array of channels to convert (sms, chat, or phone)
 * @returns Array of integration patterns for the given channels without duplicates
 */
export const channelsToIntegrations = (channels: TChannel[]): string[] => {
    return Array.from(
        new Set(
            channels.flatMap(
                /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
                (channel) => CHANNEL_TO_INTEGRATIONS[channel] ?? []
            )
        )
    );
};

export const getConnectors = (connectors?: string[]) => {
    return Array.isArray(connectors) && connectors.length > 0
        ? connectors
        : allChannels;
};

/**
 * Converts an array of integrations to their corresponding channels
 * @param integrations - Array of integration names (e.g., 'newo_chat', 'telegram', 'twilio_messenger', 'vapi')
 * @returns Array of corresponding channel types without duplicates
 */
export const integrationsToChannels = (integrations: string[]): TChannel[] => {
    return Array.from(
        new Set(
            integrations
                .map((integration) => {
                    const [baseIntegration] = integration.split('/');
                    return INTEGRATION_TO_CHANNEL[baseIntegration];
                })
                .filter(Boolean)
        )
    );
};

export const getDateRange = (value: string) => {
    const now = dayjs();
    const unitMap: Partial<Record<DateRangeKey, dayjs.ManipulateType>> = {
        [DateRangeKey.LAST_15_MINUTES]: 'minute',
        [DateRangeKey.LAST_1_HOUR]: 'hour',
        [DateRangeKey.LAST_24_HOURS]: 'hour',
        [DateRangeKey.LAST_7_DAYS]: 'day',
        [DateRangeKey.LAST_30_DAYS]: 'day'
    };

    const intervalMap: Partial<Record<DateRangeKey, number>> = {
        [DateRangeKey.LAST_15_MINUTES]: DATE_RANGE_INTERVALS.last_15_minutes,
        [DateRangeKey.LAST_1_HOUR]: DATE_RANGE_INTERVALS.last_1_hour,
        [DateRangeKey.LAST_24_HOURS]: DATE_RANGE_INTERVALS.last_24_hours,
        [DateRangeKey.LAST_7_DAYS]: DATE_RANGE_INTERVALS.last_7_days,
        [DateRangeKey.LAST_30_DAYS]: DATE_RANGE_INTERVALS.last_30_days
    };

    const unit = unitMap[value as DateRangeKey];
    const interval = intervalMap[value as DateRangeKey];

    if (!unit || !interval) {
        return { from_datetime: undefined, to_datetime: undefined };
    }

    const from =
        unit === 'day'
            ? now.subtract(interval, unit).startOf('day')
            : now.subtract(interval, unit);

    return {
        from_datetime: localDateToUTC(from.toDate()),
        to_datetime: localDateToUTC(now.toDate())
    };
};

export const getDefaultDateRangeForSessions = () => {
    const DAYS_TO_SUBTRACT = 30;
    const end = dayjs();
    const start = end.subtract(DAYS_TO_SUBTRACT, 'day');

    return [start.startOf('day').toDate(), end.endOf('day').toDate()] as [
        DateValue,
        DateValue
    ];
};
